import { Component, Input } from '@angular/core';

import { ReviewStats } from '../../interfaces/review-stats.interface';
import { formatCount, formatSharePercent } from '../../utils/review-stats-format.util';

/** Uma barra do que o usuário respondeu. */
export interface FeedbackResolutionBar {
  label: string;
  modifier: 'true-positive' | 'false-positive' | 'wont-fix';
  width: number;
  value: string;
  count: string;
}

/**
 * O card que só a visão do dev responde: quanto dos achados ELE já avaliou, e no que as
 * avaliações dele deram. Mesmo desenho da Calibragem da visão de serviço — cobertura em
 * destaque com barra, e a distribuição em barras —, com a população do próprio usuário.
 *
 * Não é o mesmo número do Painel de Curadoria. Lá a cobertura e a taxa de falso positivo
 * são do TIME inteiro, e servem para calibrar o modelo; aqui são do próprio usuário, e
 * servem para ele saber o que ainda não olhou. Duplicar o rótulo sem essa distinção faria
 * as duas telas parecerem discordar.
 */
@Component({
  selector: 'app-review-stats-feedback',
  templateUrl: './review-stats-feedback.component.html',
  styleUrls: ['./review-stats-feedback.component.css']
})
export class ReviewStatsFeedbackComponent {
  @Input() stats: ReviewStats | null = null;


  readonly cardHelp = {
    coverage:
      'Quantos dos achados deste recorte você já marcou como procedente, falso positivo ou ' +
      '"não vou corrigir".\n\n' +
      '• É a SUA cobertura de achados — o que você avaliou, não o time inteiro.\n' +
      '• Achados sem contagem processada ainda ficam de fora da conta.\n' +
      '• O veredito geral do review não entra: ele não é um achado.',
    resolutions:
      'A distribuição do que você respondeu nos achados que avaliou.\n\n' +
      '• Procede: o apontamento estava certo.\n' +
      '• Falso positivo: o apontamento estava errado. É o que mais ajuda a calibrar o modelo.\n' +
      '• Não vou corrigir: o apontamento procede, mas a correção não será feita.'
  };

  private get feedback() {
    return this.stats?.feedback ?? null;
  }

  /** O bloco só chega autenticado; sem ele não há o que mostrar (nem "—"). */
  get hasFeedback(): boolean {
    return !!this.feedback;
  }

  // ─── Cobertura ───────────────────────────────────────────────────────────

  get base(): number {
    const fb = this.feedback;
    return fb ? fb.rated + fb.unrated : 0;
  }

  /**
   * `—` em vez de "0%" quando não há achado nenhum no recorte: sem base não existe
   * cobertura, e um zero ali seria lido como "você não avaliou nada", que é outra
   * afirmação.
   */
  get coverageLabel(): string {
    if (!this.base) { return '—'; }
    return formatSharePercent(this.feedback!.rated, this.base);
  }

  get coverageWidth(): number {
    const fb = this.feedback;
    return fb && this.base ? Math.round((fb.rated / this.base) * 100) : 0;
  }

  /**
   * Com visão global o backend mede só os reviews que a pessoa pediu (decisão 0031), e o
   * texto precisa dizer isso — o resto da tela mostra os reviews de todo mundo. Lido da
   * resposta, e não deduzido do perfil: a regra é do backend.
   */
  get coverageUnit(): string {
    return this.feedback?.ownRequestsOnly
      ? 'dos achados dos reviews que você pediu, avaliados'
      : 'dos achados avaliados por você';
  }

  get ratedDetail(): string {
    const fb = this.feedback;
    if (!fb || !this.base) { return 'Nenhum achado neste recorte.'; }
    return `${formatCount(fb.rated)} de ${formatCount(this.base)} achados`;
  }

  // ─── Resoluções ──────────────────────────────────────────────────────────

  /** Sem nenhuma avaliação não há distribuição — o card mostra o convite, não zeros. */
  get hasResolutions(): boolean {
    return (this.feedback?.rated ?? 0) > 0;
  }

  /** Procede, falso positivo e "não vou corrigir", sobre o que foi avaliado. */
  get resolutionBars(): FeedbackResolutionBar[] {
    const fb = this.feedback;
    if (!fb?.rated) { return []; }
    const bar = (label: string, modifier: FeedbackResolutionBar['modifier'], n: number): FeedbackResolutionBar => ({
      label,
      modifier,
      width: Math.round((n / fb.rated) * 100),
      value: formatSharePercent(n, fb.rated),
      count: `${formatCount(n)} de ${formatCount(fb.rated)} avaliados`
    });
    return [
      bar('Procede', 'true-positive', fb.truePositive),
      bar('Falso positivo', 'false-positive', fb.falsePositive),
      bar('Não vou corrigir', 'wont-fix', fb.wontFix)
    ];
  }

  /** `resolutionBars` monta objetos novos a cada ciclo: sem isto o ngFor recriaria as barras. */
  trackByLabel(_index: number, bar: FeedbackResolutionBar): string {
    return bar.label;
  }
}
